import { Alert } from '@creditornot/cb-alert';
import { AlertCircleFilled } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';
import { localize } from 'common/utils';

import { CampaignVenueSelectItem } from './types';

export type VenueRowItemProps = {
  venueSelectItem: CampaignVenueSelectItem;
  searchValue?: string;
};

const VenueName = styled.div`
  ${typographyCss.Body3};
`;
const Address = styled.div`
  ${typographyCss.Small};
  color: ${cssThemedValue('textSecondary')};
`;

const TextWithHighlightedMatches = ({ text, searchValue }: { text: string; searchValue?: string }) => {
  if (!searchValue) {
    return <>{text}</>;
  }
  const matches = text.match(new RegExp(searchValue, 'gi'));
  const splitByHighlights = text.split(new RegExp(searchValue, 'gi'));

  return (
    <>
      {splitByHighlights.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {matches?.[index] && <b>{matches?.[index]}</b>}
        </React.Fragment>
      ))}
    </>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-grow: 1;
  align-items: center;
`;
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const VenueRowItem = ({ venueSelectItem, searchValue = '' }: VenueRowItemProps) => {
  const { locale } = useIntl();

  const { name, address, city, post_code, information, warningTag } = venueSelectItem;

  return (
    <Root>
      <Content>
        <VenueName>
          <TextWithHighlightedMatches text={localize(name, locale) || '-'} searchValue={searchValue} />
        </VenueName>
        <Address>
          <TextWithHighlightedMatches text={`${address.trim()}, ${post_code} ${city}`} searchValue={searchValue} />
        </Address>
        {!!information && (
          <Alert variant="info" size="small">
            {information}
          </Alert>
        )}
      </Content>
      {!!warningTag && (
        <Tag variant="lightYellow" size="small" icon={<AlertCircleFilled />}>
          {warningTag}
        </Tag>
      )}
    </Root>
  );
};
